import { defineStore } from 'pinia';
import { computed, ref, type ComputedRef, type Ref, type UnwrapRef } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import callEndedSoundUrl from '@/assets/call_ended.mp3';
import callAcceptedSoundUrl from '@/assets/call_accepted.mp3';
import { SessionDescriptionHandler, type SessionDescriptionHandlerOptions } from 'sip.js/lib/platform/web';
import { Info, Invitation, type InvitationAcceptOptions, Inviter, Session, SessionState, UserAgent } from 'sip.js/lib/api';
import holdSoundUrl from '@/assets/hold.mp3';
import unholdSoundUrl from '@/assets/unhold.mp3';
import type { CallMember } from '@/data/CallMember';
import { useMediaDeviceStore } from '@/stores/useMediaDeviceStore';
import { getDomain } from '@/composables/useStorageSession';
import { useContacts } from '@/api/useContacts';
import { useGetContactByNumber } from '@/api/useGetContactByNumber';
import { usePhoneStore } from '@/stores/usePhoneStore';
import muteSoundUrl from '@/assets/mute.mp3';
import unmuteSoundUrl from '@/assets/unmute.mp3';
import { usePermissionsStore } from '@/stores/usePermissionsStore';
import { httpGet, httpPost } from '@/api/http';
import { V2_CONFERENCE } from '@/constants/endpoint';
import DTMF, { type KeypadKey } from '@kimmel/dtmf';
import { useQueryClient } from '@tanstack/vue-query';
import { CALL_HISTORY_QUERY_KEY } from '@/api/useCallHistory';
import { updateTransferCall } from '@/api/transfer';
import { headsetService } from '@/libraries/headset/headset';
import { isElectron } from '@/utils/clientUtils';
import { initRequestCallConfig } from '@/electron/requestCall';
import { AgentState } from '@/data/Agent';
import { useAgentStore } from '@/stores/useAgentStore';
import { useUserStore } from '@/stores/useUserStore';
import { getNowInSeconds } from '@/utils/dateUtils';
import { useSetAgentIdleMutation } from '@/api/useAgents';
import { useDomainOptions } from '@/api/useDomainOptions';
import { isAgentAvailable } from '@/utils/agentUtils.ts';

export enum CallEndReason {
  REMOTE_DECLINED,
  SERVER_ERROR,
  REMOTE_BUSY,
  REMOTE_TEMPORARILY_UNAVAILABLE,
  REQUEST_TERMINATED,
  UNKNOWN_REJECTION,
  LOCAL_HUNG_UP,
  REMOTE_HUNG_UP,
  SOUND_ERROR,
}

export enum CallModalType {
  ADD_CONTACT = 'addContact',
  COLD_TRANSFER = 'coldTransfer',
  WARM_TRANSFER = 'warmTransfer',
}

export type ApiConferenceMember = {
  number: string;
  channelUuid: string;
  established: boolean;
};

export interface Call {
  session: UnwrapRef<Session>;
  sessionState: UnwrapRef<SessionState>;
  remoteStream: MediaStream;
  tracks: MediaStreamTrack[];
  initialStartTime: number | null;
  establishedStartTime: number | null;
  isTransferTarget: boolean;
  incoming: boolean;
  eavesdrop: boolean;
  otherPartyOverride: { number: string; name?: string } | null;
  audioElement: HTMLAudioElement;
  ringtones: HTMLAudioElement[] | null;
  ringback: HTMLAudioElement | null;
  currentlyPlayingSoundToRemote: boolean;
  holding: boolean;
  muted: boolean;
  speaker: boolean;
  hungup: boolean;
  transferTarget: UnwrapRef<Call> | null;
  callId: string;
  conferenceMembers: ApiConferenceMember[] | null;
  conferenceUuid: string | null;
  pickupGroups: string | null;
  destinationNumber: string | null;
  isPickupGroupCall: boolean;
  inviteParticipants: { phone_number: string }[] | null;
  endReason: UnwrapRef<CallEndReason> | null;
}

enum EventType {
  TRANSFER = 'TRANSFER',
  CONFERENCE = 'CONFERENCE',
}

interface BaseEvent {
  type: EventType;
}

interface TransferEvent extends BaseEvent {
  type: EventType.TRANSFER;
  actualDestination: string;
}

interface ConferenceEvent extends BaseEvent {
  type: EventType.CONFERENCE;
  conferenceUuid: string;
}

type SipInfoEvent = TransferEvent | ConferenceEvent;

interface CallStore {
  createNewCall: (
    session: UnwrapRef<Session>,
    incoming: boolean,
    ringtones: HTMLAudioElement[] | null,
    ringback: HTMLAudioElement | null,
    isTransferTarget?: boolean,
    otherPartyOverride?: { number: string; name?: string } | null
  ) => Ref<Call>;
  playSoundToLocal: (soundUrl: string, volume: number, call: UnwrapRef<Call> | null) => void;
  addCall: (call: UnwrapRef<Call>) => void;
  getCalls: ComputedRef<UnwrapRef<Call>[]>;
  callMembers: (call?: UnwrapRef<Call> | null) => UnwrapRef<CallMember>[];
  selectOutput: (outputId: string, call: UnwrapRef<Call> | null) => void;
  selectInput: (inputId: string | null, call: UnwrapRef<Call> | null) => Promise<void>;
  displayOptions: (call: UnwrapRef<Call>) => {
    toast: boolean;
    notification: boolean;
    sideOnDesktop: boolean;
    switchboard: boolean;
    callcenter: boolean;
  };
  acceptIncoming: (call?: UnwrapRef<Call> | null) => void;
  end: (call?: UnwrapRef<Call> | null) => Promise<void>;
  referrer: (call: UnwrapRef<Call> | null) => string;
  diverter: (call: UnwrapRef<Call> | null) => string;
  startWarmTransfer: (call: UnwrapRef<Call> | null | undefined, target: string | Call) => UnwrapRef<Call> | null;
  setHold: (call?: UnwrapRef<Call> | null, hold?: boolean) => Promise<void>;
  canCompleteWarmTransfer: (call: UnwrapRef<Call>) => boolean;
  completeWarmTransfer: (call: UnwrapRef<Call> | null | undefined) => void;
  coldTransfer: (call: UnwrapRef<Call> | null | undefined, target: string) => void;
  isIncomingRinging: (call?: UnwrapRef<Call> | null) => boolean;
  setMute: (call?: UnwrapRef<Call> | null, mute?: boolean) => void;
  sendDtmf: (call: UnwrapRef<Call> | null | undefined, code: string) => void;
  addMember: (call: UnwrapRef<Call> | null | undefined, target: string) => Promise<void>;
  kickMember: (call: UnwrapRef<Call> | null | undefined, callMember: UnwrapRef<CallMember>) => Promise<void>;
  forceLocalTerminationIfStuck: (call: UnwrapRef<Call> | null) => void;
}

export const useCallStore = defineStore('call', (): CallStore => {
  const calls = ref<Call[]>([]);
  const queryClient = useQueryClient();
  const $mediaDeviceStore = useMediaDeviceStore();
  const $agentStore = useAgentStore();
  const $phoneStore = usePhoneStore();
  const $permissionStore = usePermissionsStore();
  const $contacts = useContacts();
  const { data: options } = useDomainOptions();
  const $userStore = useUserStore();
  const { getContactByPhoneNumber } = useGetContactByNumber($contacts.data);
  const { mutate: setAgentStatusToIdle } = useSetAgentIdleMutation();

  if (isElectron()) {
    initRequestCallConfig();
  }

  const createNewCall = (
    session: UnwrapRef<Session>,
    incoming: boolean,
    ringtones: HTMLAudioElement[] | null,
    ringback: HTMLAudioElement | null,
    isTransferTarget = false,
    otherPartyOverride: { number: string; name?: string } | null = null
  ) => {
    const remoteStream = new MediaStream();
    const audioElement = new Audio();
    audioElement.srcObject = remoteStream;
    const inviteParticipants = null;
    const callId = uuidv4();
    const holding = false;

    const call: Ref<Call> = ref<Call>({
      callId,
      session,
      incoming,
      ringtones,
      ringback,
      isTransferTarget,
      otherPartyOverride,
      remoteStream,
      audioElement,
      tracks: [],
      establishedStartTime: null,
      initialStartTime: getNowInSeconds(),
      currentlyPlayingSoundToRemote: false,
      holding,
      muted: false,
      eavesdrop: false,
      speaker: false,
      hungup: false,
      transferTarget: null,
      conferenceMembers: null,
      conferenceUuid: null,
      inviteParticipants,
      pickupGroups: null,
      destinationNumber: null,
      isPickupGroupCall: false,
      endReason: null,
      sessionState: SessionState.Initial,
    });

    call.value.session.stateChange.addListener((state) => {
      sessionStateChanged(state, call.value as Call);
    });
    call.value.session.delegate = {
      onInfo: (request) => receiveInfo(request, call.value as Call),
      onBye: (bye) => {
        console.log('Call ended Bye: ', bye.request.data);
        playSoundToLocal(callEndedSoundUrl, 0.3, call.value as Call);
        bye.accept();
      },
    };
    console.log('Delegate set');

    if (call.value.session instanceof Invitation) {
      const participants = call.value.session.request.getHeader('X-WTG-Participants');
      if (participants) {
        call.value.inviteParticipants = JSON.parse(atob(participants));
      }
      const pickupGroups = call.value.session.request.getHeader('X-Called-Groups');
      if (pickupGroups) {
        call.value.pickupGroups = pickupGroups;
      }
      const destinationNumber = call.value.session.request.getHeader('X-Destination-Number');
      if (destinationNumber) {
        call.value.destinationNumber = destinationNumber;
      }
    }

    if (call.value.pickupGroups && call.value.destinationNumber != $userStore._loggedInUser?.extension?.extension) {
      call.value.isPickupGroupCall = true;
    }

    return call;
  };

  const shouldSetAgentToIdle = (wrapUpAutomatic?: boolean, wrapUpTimerSeconds?: number) => !(wrapUpAutomatic && wrapUpTimerSeconds === 0);

  const calculateDowntimeEnd = (wrapUpAutomatic?: boolean, wrapUpTimerSeconds?: number) => {
    return wrapUpAutomatic && wrapUpTimerSeconds ? getNowInSeconds() + wrapUpTimerSeconds : null;
  };

  const sessionStateChanged = (state: UnwrapRef<SessionState>, call: UnwrapRef<Call>) => {
    call.sessionState = state;
    console.log(`Call: UnwrapRef<Session> state changed to ${state}`);
    console.log('Delegate:', call.session.delegate);
    switch (state) {
      case SessionState.Initial: {
        call.initialStartTime = getNowInSeconds();
        break;
      }
      case SessionState.Establishing: {
        stopRingtone(call);
        break;
      }
      case SessionState.Established: {
        stopRingback(call);
        setupRemoteMedia(call);
        call.establishedStartTime = getNowInSeconds();
        playSoundToLocal(callAcceptedSoundUrl, 0.3, call);
        callActivated(call.callId);
        break;
      }
      case SessionState.Terminating: {
        break;
      }
      case SessionState.Terminated: {
        finalizeCallTermination(call);
        break;
      }
      default: {
        throw new Error('Unknown session state.');
      }
    }
  };

  // Runs the same cleanup as the SessionState.Terminated case above, but
  // callable directly instead of only in reaction to the session's own
  // stateChange event — see forceLocalTerminationIfStuck() below for why
  // that's needed.
  const finalizeCallTermination = (call: UnwrapRef<Call>): void => {
    console.log('Session terminated');
    if ($agentStore._agent?.state === AgentState.IN_QUEUE_CALL) {
      const { wrapUpAutomatic, wrapUpTimerSeconds } = options.value ?? {};

      if (shouldSetAgentToIdle(wrapUpAutomatic, wrapUpTimerSeconds)) {
        setAgentStatusToIdle({
          agentUuid: $agentStore._agent.uuid,
          downtimeEnd: calculateDowntimeEnd(wrapUpAutomatic, wrapUpTimerSeconds),
        });
      }
    }
    if (call.endReason === null) {
      // If there is no other reason, then the remote probably just hung up.
      call.endReason = CallEndReason.REMOTE_HUNG_UP;
      queryClient.invalidateQueries({ queryKey: [CALL_HISTORY_QUERY_KEY] });
    }

    if (call.transferTarget) {
      call.transferTarget.isTransferTarget = false;
    }
    cleanupMedia(call);
    stopRingback(call);
    stopRingtone(call);

    call.hungup = true;
    // before we will remove the call we need to give some time to display to the client status that the call was terminated.
    // Filtered by callId (not session state) since forceLocalTerminationIfStuck()
    // below is used precisely when the session's own state never reaches
    // Terminated.
    setTimeout(() => {
      calls.value = calls.value.filter((c) => c.callId !== call.callId);
    }, 1_000);
  };

  // Fallback for when sip.js's own final-response handling silently drops an
  // outgoing call's rejection because its internal state guard doesn't
  // match at the moment the response arrives — see
  // node_modules/sip.js/lib/api/inviter.js Inviter.onReject(): if
  // `this.state` isn't Establishing/Terminating right then, it logs an
  // error and returns WITHOUT calling stateTransition(Terminated), even
  // though it still unconditionally invokes our own requestDelegate.onReject
  // callback afterwards. When that happens, sessionStateChanged() above
  // never fires for this call, so it's stuck locally — ringback still
  // playing, "calling" UI never clears — even though the far end genuinely
  // rejected. usePhoneStore.ts calls this from that requestDelegate.onReject
  // callback as a safety net; if the normal path already ran,
  // finalizeCallTermination() already set call.hungup and this is a no-op.
  const forceLocalTerminationIfStuck = (call: UnwrapRef<Call> | null): void => {
    if (call == null || call.hungup) return;
    console.warn(`Call ${call.callId}: final response received but session never transitioned to Terminated; forcing local cleanup.`);
    finalizeCallTermination(call);
  };

  const setHold = async (call?: UnwrapRef<Call> | null, hold: boolean = false): Promise<void> => {
    if (!call) {
      console.error('There is no call to set it on Hold');
      return Promise.resolve();
    }
    console.log('Setting hold to ' + hold);
    // Just resolve if we are already in correct state
    if (call.holding === hold || call.session.state !== SessionState.Established) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const options = {
        requestDelegate: {
          onAccept: () => {
            call.holding = hold;
            enableReceiverTracks(!call.holding, call.session);
            enableSenderTracks(!call.holding && !call.muted, call.session);
            if (hold) {
              playSoundToLocal(holdSoundUrl, 0.15, call);
              resolve();
            } else {
              playSoundToLocal(unholdSoundUrl, 0.15, call);
              playSoundToRemote(unholdSoundUrl, 500, call).then(() => {
                resolve();
              });
            }
          },
          onReject: () => {
            enableReceiverTracks(!call.holding, call.session);
            enableSenderTracks(!call.holding && !call.muted, call.session);
            resolve();
          },
        },
      };

      const sessionDescriptionHandlerOptions: UnwrapRef<SessionDescriptionHandlerOptions> = call.session.sessionDescriptionHandlerOptionsReInvite;
      sessionDescriptionHandlerOptions.hold = hold;
      call.session.sessionDescriptionHandlerOptionsReInvite = sessionDescriptionHandlerOptions;

      if (hold) {
        playSoundToRemote(holdSoundUrl, 1000, call).then(() => {
          console.log('Sending hold reinvite...');
          call.session.invite(options);
          console.log('Sent hold reinvite');
          resolve();
        });
      } else {
        call.session.invite(options).then(() => callActivated(call.callId));
        resolve();
      }
    });
  };

  const callActivated = (callId: string): void => {
    calls.value.forEach((call) => {
      if (call.callId !== callId) {
        return setHold(call as Call, true);
      }
    });
  };

  const coldTransfer = (call: UnwrapRef<Call> | null | undefined, target: string): void => {
    if (call == null) {
      console.error('There is no call to start warm transfer.');
      return;
    }
    const targetUri = UserAgent.makeURI($phoneStore.canonizeTarget(target));
    if (targetUri) {
      call.session.refer(targetUri);
    } else {
      throw Error("Couldn't build URI");
    }
  };

  const startWarmTransfer = (call: UnwrapRef<Call> | null | undefined, target: string | Call): UnwrapRef<Call> | null => {
    if (!call) {
      console.error('There is no call to start warm transfer.');
      return null;
    }
    if (typeof target === 'object' && 'callId' in target) {
      call.transferTarget = target;
      target.isTransferTarget = true;
      setHold(call, true);
    } else {
      call.transferTarget = $phoneStore.call(target, true);
      if (call.transferTarget !== null) {
        setHold(call, true);
      }
    }
    return call.transferTarget;
  };

  const canCompleteWarmTransfer = (call: UnwrapRef<Call>): boolean => {
    return (
      call.session.state === SessionState.Established &&
      call.transferTarget !== null &&
      call.transferTarget.session.state === SessionState.Established
    );
  };

  const completeWarmTransfer = (call: UnwrapRef<Call> | null | undefined): void => {
    if (!call) return;
    if (call.transferTarget === null) {
      throw Error('No warm transfer started.');
    } else if (!canCompleteWarmTransfer(call)) {
      throw Error('Not ready to complete transfer');
    } else {
      call.session.refer(call.transferTarget.session as Session);
      const transfereeNumber = call.otherPartyOverride ? call.otherPartyOverride.number : call.session.remoteIdentity.uri.user;
      const transferTargetNumber = call.transferTarget.session.remoteIdentity.uri.user;
      updateTransferCall(transfereeNumber, transferTargetNumber);
    }
  };

  const receiveInfo = (request: Info, call: UnwrapRef<Call>) => {
    const message: SipInfoEvent = JSON.parse(request.request.body);
    switch (message.type) {
      case EventType.TRANSFER:
        call.otherPartyOverride = { number: message.actualDestination };
        break;
      case EventType.CONFERENCE:
        getConferenceMembers(message.conferenceUuid).then((conferenceMembers) => (call.conferenceMembers = conferenceMembers));
        call.conferenceUuid = message.conferenceUuid;
        break;
    }
    request.accept();
  };

  const stopRingtone = (call: UnwrapRef<Call> | null): void => {
    if (call === null) return;
    console.log('Stopping ringtone(s)', call.ringtones);
    if (call.ringtones !== null) {
      console.log('There are ringtones');
      call.ringtones.forEach((ringtone) => {
        ringtone.pause();
        ringtone.src = '';
        ringtone.remove();
      });
      call.ringtones = null;
    }
  };

  const cleanupMedia = (call: UnwrapRef<Call> | null) => {
    if (call == null) return;
    call.tracks.forEach((track) => {
      track.stop();
      call.remoteStream.removeTrack(track);
    });
    call.audioElement.pause();
  };

  const stopRingback = (call: UnwrapRef<Call> | null) => {
    if (call === null) return;
    if (call.ringback !== null) {
      call.ringback.pause();
      call.ringback.remove();
      call.ringback = null;
    }
  };

  const selectInput = async (inputId: string | null, call: UnwrapRef<Call> | null) => {
    if (call === null) return;
    if (call.session.state === SessionState.Established) {
      const newConstraints = { audio: $mediaDeviceStore.buildAudioConstraints(inputId) };

      const sdh = call.session.sessionDescriptionHandler;
      if (!(sdh instanceof SessionDescriptionHandler)) {
        throw new Error("Session's session description handler not instance of SessionDescriptionHandler.");
      }
      const peerConnection = sdh.peerConnection;

      if (!peerConnection) {
        throw new Error('Peer connection closed.');
      }

      const sender = peerConnection.getSenders().find((s) => {
        // console.log(s.track!.kind);
        return s.track!.kind == 'audio';
      });

      sender!.track!.stop();

      const stream = await navigator.mediaDevices.getUserMedia(newConstraints);
      const track = stream.getAudioTracks()[0]!;
      // A new track is always on, so keep the call muted / on hold if it was.
      track.enabled = !call.holding && !call.muted;

      await sender!.replaceTrack(track);
    }
  };

  const selectOutput = (outputId: string, call: UnwrapRef<Call> | null) => {
    if (call === null) return;
    if (call.ringback === null) {
      console.log('No ringback to set.');
    } else if ('setSinkId' in call.ringback) {
      console.log('Ringback sink id set to', outputId, 'for call', call.callId);
      // Swallow benign AbortError if torn down mid-switch.
      (call.ringback as any).setSinkId(outputId).catch(() => undefined);
    } else {
      console.error("Can't set ringback sink id");
    }

    if ('setSinkId' in call.audioElement) {
      console.log('Sink id set to', outputId, 'for call', call.callId);
      (call.audioElement as any).setSinkId(outputId).catch(() => undefined);
    } else {
      console.error("Can't set sink id");
    }
  };

  const enableSenderTracks = (enable: boolean, session: UnwrapRef<Session>) => {
    if (!session) {
      throw new Error('Session does not exist.');
    }
    const sessionDescriptionHandler = session.sessionDescriptionHandler;
    if (!(sessionDescriptionHandler instanceof SessionDescriptionHandler)) {
      throw new Error("Session's session description handler not instance of SessionDescriptionHandler.");
    }
    const peerConnection = sessionDescriptionHandler.peerConnection;
    if (!peerConnection) {
      throw new Error('Peer connection closed.');
    }
    peerConnection.getSenders().forEach((sender) => {
      if (sender.track) {
        sender.track.enabled = enable;
      }
    });
  };

  const enableReceiverTracks = (enable: boolean, session: UnwrapRef<Session>) => {
    if (!session) {
      throw new Error('Session does not exist.');
    }
    const sessionDescriptionHandler = session.sessionDescriptionHandler;
    if (!(sessionDescriptionHandler instanceof SessionDescriptionHandler)) {
      throw new Error("Session's session description handler not instance of SessionDescriptionHandler.");
    }
    const peerConnection = sessionDescriptionHandler.peerConnection;
    if (!peerConnection) {
      throw new Error('Peer connection closed.');
    }
    peerConnection.getReceivers().forEach((receiver) => {
      if (receiver.track) {
        receiver.track.enabled = enable;
      }
    });
  };

  const playSoundToRemote = async (soundUrl: string, duration: number, call: UnwrapRef<Call> | null): Promise<void> => {
    if (call === null) return;
    return new Promise((resolve) => {
      if (call.currentlyPlayingSoundToRemote) {
        return; // Not playing two sounds at once.
      }
      if (!call.session) {
        throw new Error('Session does not exist.');
      }
      const sessionDescriptionHandler = call.session.sessionDescriptionHandler;
      if (!(sessionDescriptionHandler instanceof SessionDescriptionHandler)) {
        throw new Error("Session's session description handler not instance of SessionDescriptionHandler.");
      }
      const peerConnection = sessionDescriptionHandler.peerConnection;
      if (!peerConnection) {
        throw new Error('Peer connection closed.');
      }
      const mp3Audio = new Audio(soundUrl);
      const audioContext = new AudioContext();
      const mp3AudioSource = audioContext.createMediaElementSource(mp3Audio);
      const mp3AudioDestination = audioContext.createMediaStreamDestination();
      mp3AudioSource.connect(mp3AudioDestination);
      const mp3AudioTrack = mp3AudioDestination.stream.getAudioTracks()[0]!;

      const sender = peerConnection.getSenders()[0]!;
      const trackBefore = sender.track;
      call.currentlyPlayingSoundToRemote = true;
      // Swallow benign AbortError if the call ends mid-playback.
      sender.replaceTrack(mp3AudioTrack).catch(() => undefined);
      mp3Audio.play().catch(() => undefined);
      setTimeout(() => {
        sender.replaceTrack(trackBefore).catch(() => undefined);
        call.currentlyPlayingSoundToRemote = false;
        resolve();
      }, duration);
    });
  };

  const playSoundToLocal = (soundUrl: string, volume: number, call: UnwrapRef<Call> | null): void => {
    if (call === null) return;
    const soundAudio = new Audio(soundUrl);
    soundAudio.volume = volume;
    soundAudio.loop = false;

    if ($mediaDeviceStore.selectedAudioOutputId !== null) {
      if ('setSinkId' in call.audioElement) {
        console.log('Play sound sink id set to', $mediaDeviceStore.selectedAudioOutputId, 'for call', call.callId);
        (call.audioElement as any).setSinkId($mediaDeviceStore.selectedAudioOutputId).catch(() => undefined);
      } else {
        console.error("Can't set sink id");
      }
    }

    // Swallow benign AbortError if teardown interrupts playback.
    soundAudio.play().catch(() => undefined);
  };

  const setupRemoteMedia = (call: UnwrapRef<Call> | null) => {
    if (call === null) return;
    const sdh = call.session.sessionDescriptionHandler;
    if (!(sdh instanceof SessionDescriptionHandler)) {
      throw new Error("Session's session description handler not instance of SessionDescriptionHandler.");
    }
    const peerConnection = sdh.peerConnection;

    if (!peerConnection) {
      throw new Error('Peer connection closed.');
    }

    peerConnection.getReceivers().forEach((receiver) => {
      if (receiver.track) {
        console.log('adding track!');
        call.remoteStream.addTrack(receiver.track);
        call.tracks.push(receiver.track);
      }
    });
    if ($mediaDeviceStore.selectedAudioOutputId !== null) {
      selectOutput($mediaDeviceStore.selectedAudioOutputId, call);
    }

    // Swallow benign AbortError if the call ends before playback starts.
    call.audioElement.play().catch(() => undefined);
  };

  const displayOptions = (
    call: UnwrapRef<Call>
  ): {
    toast: boolean;
    notification: boolean;
    sideOnDesktop: boolean;
    switchboard: boolean;
    callcenter: boolean;
  } => {
    const isAvailable = !!$agentStore._agent?.status && isAgentAvailable($agentStore._agent?.status);
    const showOnSwitchboard = $permissionStore._switchboardEnabled && isAvailable;
    const showOnCallcenter = $permissionStore._isAgent && isAvailable;

    return {
      toast: isIncomingRinging(call), // And hide if showOnSwitchboard and switchboard is shown or showOnCallcenter and callcenter is shown, but we can only do that in the frontend
      notification: isIncomingRinging(call),
      sideOnDesktop: !isIncomingRinging(call) && !call.isTransferTarget && !showOnSwitchboard && !showOnCallcenter && !call.eavesdrop,
      switchboard: showOnSwitchboard,
      callcenter: showOnCallcenter || call.eavesdrop,
    };
  };

  const getConferenceMembers = async (conferenceUuid: string): Promise<ApiConferenceMember[]> => {
    return await httpGet<ApiConferenceMember[]>(`${V2_CONFERENCE}/${conferenceUuid}/members`);
  };

  const addMember = async (call: UnwrapRef<Call> | null | undefined, target: string): Promise<void> => {
    if (call == null) return;
    await httpPost(`${V2_CONFERENCE}/${target}/add`, { conferenceUuid: call.conferenceUuid });
  };

  const kickMember = async (call: UnwrapRef<Call> | null | undefined, callMember: UnwrapRef<CallMember>): Promise<void> => {
    if (call == null) return;
    if (callMember.canBeKicked && callMember.channelUuid) {
      await httpPost(`${V2_CONFERENCE}/${callMember.channelUuid}/kick`);
    }
  };

  const callMembers = (call?: UnwrapRef<Call> | null): UnwrapRef<CallMember>[] => {
    if (!call) return [];
    // todo needs to be refactored
    if (call.conferenceMembers && call.conferenceMembers.length > 1) {
      const numberOfActiveMembers = call.conferenceMembers.filter((member) => member.established).length;

      return call.conferenceMembers
        .filter((member) => member.number !== $userStore._loggedInUser.extension?.extension)
        .map((member) => ({
          number: member.number,
          contact: getContactByPhoneNumber(member.number) ?? null,
          status: !member.established ? 'dialing' : call.holding ? 'held' : ('active' as const),
          call: call,
          canBeKicked: numberOfActiveMembers > 2 || !member.established,
          channelUuid: member.channelUuid,
        }));
    } else if (call?.inviteParticipants) {
      const primaryUri = call.session.remoteIdentity.uri.toString();
      const primaryNumber = primaryUri.replace('sip:', '').replace('@' + getDomain(), '');

      const result: UnwrapRef<CallMember>[] = [
        {
          number: primaryNumber,
          contact: getContactByPhoneNumber(primaryNumber) ?? null,
          status: 'active',
          call: call,
          canBeKicked: false,
        },
      ];
      for (const participant of call.inviteParticipants) {
        if (participant.phone_number !== primaryNumber) {
          result.push({
            number: participant.phone_number,
            contact: getContactByPhoneNumber(participant.phone_number) ?? null,
            status: 'active',
            call: call,
            canBeKicked: false,
          });
        }
      }
      return result;
    } else if (call?.otherPartyOverride) {
      const overriddenMember: UnwrapRef<CallMember> = {
        number: call.otherPartyOverride.number,
        contact: getContactByPhoneNumber(call.otherPartyOverride.number) ?? null,
        status: call.holding ? 'held' : 'active',
        call: call,
        canBeKicked: false,
      };
      if (call.otherPartyOverride.name) {
        overriddenMember.name = call.otherPartyOverride.name;
      }

      return [overriddenMember];
    } else {
      const uri = call.session.remoteIdentity.uri.toString();
      const number = uri.replace('sip:', '').split('@')[0]!;
      return [
        {
          number: number,
          name: call.session.remoteIdentity.displayName,
          contact: getContactByPhoneNumber(number) ?? null,
          status: call.holding ? 'held' : 'active',
          call: call,
          canBeKicked: false,
        },
      ];
    }
  };

  const getHeaderValue = (call: UnwrapRef<Call> | null, header: string): string => {
    if (!(call?.session instanceof Invitation)) return '';
    return call.session.request.getHeader(header) ?? '';
  };

  const referrer = (call: UnwrapRef<Call> | null): string => getHeaderValue(call, 'X-Referred-By');

  const diverter = (call: UnwrapRef<Call> | null): string => getHeaderValue(call, 'X-Diversion');

  const isIncomingRinging = (call?: UnwrapRef<Call> | null): boolean => {
    return call?.session instanceof Invitation && call.sessionState === SessionState.Initial;
  };
  const acceptIncoming = (call?: UnwrapRef<Call> | null): void => {
    if (call == null) return;
    headsetService?.answerCall(call.callId);
    if (!(call.session instanceof Invitation)) {
      throw new TypeError("This is not a new Invitation and can't be accepted");
    } else if (call.session.state !== SessionState.Initial && call.session.state !== SessionState.Establishing) {
      // todo do we need this condition?
      console.log('Call is already accepted');
    } else {
      const audioConstraints = $mediaDeviceStore.buildAudioConstraints();
      console.log('Audio input constraints:', JSON.stringify(audioConstraints));
      const acceptOptions: InvitationAcceptOptions = {
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: audioConstraints,
            video: false,
          },
        },
      };

      call.session.accept(acceptOptions).catch(() => {
        call.endReason = CallEndReason.SOUND_ERROR;
      });
    }
  };

  const setMute = (call?: UnwrapRef<Call> | null, mute: boolean = false): void => {
    if (call == null) return;
    enableSenderTracks(!mute, call.session);
    call.muted = mute;
    if (mute) {
      playSoundToLocal(muteSoundUrl, 0.15, call);
    } else {
      playSoundToLocal(unmuteSoundUrl, 0.15, call);
    }
  };

  const end = async (call?: UnwrapRef<Call> | null): Promise<void> => {
    if (call == null) return;
    headsetService?.endCall(call.callId);

    console.log('Ending in session state ' + call.session.state);
    call.endReason = CallEndReason.LOCAL_HUNG_UP;

    switch (call.session.state) {
      case SessionState.Initial:
      case SessionState.Establishing:
        playSoundToLocal(callEndedSoundUrl, 0.3, call);
        if (call.session instanceof Inviter) {
          return call.session.cancel();
        } else if (call.session instanceof Invitation) {
          return call.session.reject({ statusCode: 603, extraHeaders: ['WTG-Reject: Immediate'] });
        }
        break;
      case SessionState.Established:
        console.log('Ending, playing sound');
        // An established session
        playSoundToLocal(callEndedSoundUrl, 0.3, call);
        return new Promise((resolve, reject) => {
          call.session.bye({
            requestDelegate: {
              onAccept: () => {
                console.log('BYE accepted');
                resolve();
              },
              onReject: () => {
                console.log('BYE rejected');
                reject();
              },
            },
          });
        });
      case SessionState.Terminating:
      case SessionState.Terminated:
        // Cannot terminate a session that is already terminated
        break;
    }
  };

  const sendDtmf = (call: UnwrapRef<Call> | null | undefined, code: string): void => {
    if (call == null) return;
    const dtmfPlayer = new DTMF();
    const key = code as KeypadKey;
    dtmfPlayer.play(key);
    setTimeout(() => {
      dtmfPlayer.stop();
    }, 200);
    call.session.sessionDescriptionHandler?.sendDtmf(code);
  };

  const addCall = (call: UnwrapRef<Call>) => {
    calls.value = [...calls.value, call];
  };

  const getCalls = computed(() => calls.value);

  return {
    createNewCall,
    playSoundToLocal,
    addCall,
    getCalls,
    callMembers,
    selectOutput,
    selectInput,
    displayOptions,
    acceptIncoming,
    end,
    referrer,
    diverter,
    startWarmTransfer,
    setHold,
    canCompleteWarmTransfer,
    completeWarmTransfer,
    coldTransfer,
    isIncomingRinging,
    setMute,
    sendDtmf,
    addMember,
    kickMember,
    forceLocalTerminationIfStuck,
  };
});
